import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

import AuthGuard from './AuthGuard';

// project imports
import Loadable from 'components/Loadable';
import RouteErrorBoundary from 'components/RouteErrorBoundary';
import DashboardLayout from 'layout/Dashboard';
import { getCurrentAccess, getStoredUser } from 'utils/accessControl';
import Authorized from 'components/access/Authorized';
import SubscriptionModuleGuard from 'components/subscription/SubscriptionModuleGuard';

// ==============================|| DASHBOARD ||============================== //

const DashboardDefault = Loadable(
  lazy(() => import('pages/dashboard/default'))
);

const DoctorDashboard = Loadable(
  lazy(() => import('pages/dashboard/doctor'))
);

const LabAssistantDashboard = Loadable(
  lazy(() => import('pages/dashboard/lab-assistant'))
);

const PatientDashboard = Loadable(
  lazy(() => import('pages/dashboard/patient'))
);
const PharmacyDashboard = Loadable(
  lazy(() => import('pages/dashboard/pharmacy'))
);

// ==============================|| COMPONENTS ||============================== //

const Color = Loadable(
  lazy(() => import('pages/component-overview/color'))
);

const Typography = Loadable(
  lazy(() => import('pages/component-overview/typography'))
);

const Shadow = Loadable(
  lazy(() => import('pages/component-overview/shadows'))
);

// ==============================|| EXTRA PAGES ||============================== //

const SamplePage = Loadable(
  lazy(() => import('pages/extra-pages/sample-page'))
);

// ==============================|| PROFILE ||============================== //

const ViewProfile = Loadable(
  lazy(() => import('pages/ViewProfile'))
);

const EditProfile = Loadable(
  lazy(() => import('pages/EditProfile'))
);

const ChangePassword = Loadable(
  lazy(() => import('pages/auth/ChangePassword'))
);

const SettingsPage = Loadable(
  lazy(() => import('pages/Settings/index'))
);

const ClinicProfile = Loadable(
  lazy(() => import('pages/AdminManagement/ClinicProfile'))
);

const Expenses = Loadable(
  lazy(() => import('pages/Billing/Expenses'))
);

const InvoicesList = Loadable(
  lazy(() => import('pages/Billing/InvoicesList'))
);

const QuickBill = Loadable(
  lazy(() => import('pages/Billing/QuickBill'))
);

const Transactions = Loadable(
  lazy(() => import('pages/Billing/Transactions'))
);

const Subscription = Loadable(
  lazy(() => import('pages/Subscription'))
);

// ==============================|| APPOINTMENTS ||============================== //

const CreateAppointment = Loadable(
  lazy(() => import('pages/Appointments/CreateAppointment'))
);

const MyAppointments = Loadable(
  lazy(() => import('pages/Appointments/MyAppointments'))
);

const AppointmentReview = Loadable(
  lazy(() => import('pages/Appointments/AppointmentReview'))
);

const AppointmentDetails = Loadable(
  lazy(() => import('pages/Appointments/AppointmentDetails'))
);

const AppointmentQueue = Loadable(
  lazy(() => import('pages/Appointments/AppointmentQueue'))
);

const AppointmentCalendar = Loadable(
  lazy(() => import('pages/Appointments/AppointmentCalendar'))
);

// ==============================|| CLINICAL MANAGEMENT ||============================== //

const MedicalRecords = Loadable(
  lazy(() => import('pages/ClinicalManagement/MedicalRecords'))
);

const Prescriptions = Loadable(
  lazy(() => import('pages/ClinicalManagement/Prescriptions'))
);

const MedicineInventory = Loadable(
  lazy(() => import('pages/Pharmacy/MedicineInventory'))
);

const PrescriptionFulfillment = Loadable(
  lazy(() => import('pages/Pharmacy/PrescriptionFulfillment'))
);

const PharmacyBilling = Loadable(
  lazy(() => import('pages/Pharmacy/PharmacyBilling'))
);

const PharmacyReports = Loadable(
  lazy(() => import('pages/Pharmacy/PharmacyReports'))
);

const ReportsDashboard = Loadable(
  lazy(() => import('pages/Reports/ReportsDashboard'))
);

const LabTests = Loadable(
  lazy(() => import('pages/ClinicalManagement/LabTests'))
);

const LabRequest = Loadable(
  lazy(() => import('pages/ClinicalManagement/LabRequest'))
);

const MedicalReports = Loadable(
  lazy(() => import('pages/ClinicalManagement/MedicalReports'))
);

const PatientHistory = Loadable(
  lazy(() => import('pages/ClinicalManagement/PatientHistory'))
);

const ClinicalWorkflow = Loadable(
  lazy(() => import('pages/ClinicalManagement/ClinicalWorkflow'))
);

const ExternalLabWork = Loadable(
  lazy(() => import('pages/ClinicalManagement/ExternalLabWork'))
);

const InventoryDashboard = Loadable(
  lazy(() => import('pages/Inventory/InventoryDashboard'))
);

// ==============================|| ACCESS MANAGEMENT ||============================== //

const ModulesUI = Loadable(
  lazy(() => import('pages/AccessManagement/Modules'))
);
const PermissionsUI = Loadable(
  lazy(() => import('pages/AccessManagement/Permissions'))
);
const RolePermissionsUI = Loadable(
  lazy(() => import('pages/AccessManagement/RolePermissions'))
);
const ClinicTypePermissionsUI = Loadable(
  lazy(() => import('pages/AccessManagement/ClinicTypePermissions'))
);
const UserPermissionOverridesUI = Loadable(
  lazy(() => import('pages/AccessManagement/UserPermissionOverrides'))
);
const MenusUI = Loadable(
  lazy(() => import('pages/AccessManagement/Menus'))
);
const RoleMenusUI = Loadable(
  lazy(() => import('pages/AccessManagement/RoleMenus'))
);

// ==============================|| ADMIN MANAGEMENT ||============================== //

const UserManagement = Loadable(
  lazy(() => import('pages/UserManagement'))
);

const DoctorManagement = Loadable(
  lazy(() => import('pages/AdminManagement/Doctors'))
);

const PatientManagement = Loadable(
  lazy(() => import('pages/AdminManagement/Patients'))
);

const StaffManagement = Loadable(
  lazy(() => import('pages/AdminManagement/Staff'))
);

const ClinicManagement = Loadable(
  lazy(() => import('pages/AdminManagement/Clinics'))
);

const TreatmentManagement = Loadable(
  lazy(() => import('pages/AdminManagement/TreatmentManagement'))
);

const ClinicTypes = Loadable(
  lazy(() => import('pages/AdminManagement/ClinicTypes'))
);

const ClinicSetupWizard = Loadable(
  lazy(() => import('pages/AdminManagement/ClinicSetupWizard'))
);

// ==============================|| DOCTOR MANAGEMENT ||============================== //

const DoctorsList = Loadable(
  lazy(() => import('pages/DoctorManagement/DoctorsList'))
);

const AvailabilityManagement = Loadable(
  lazy(() => import('pages/DoctorManagement/AvailabilityManagement'))
);

const BreakManagement = Loadable(
  lazy(() => import('pages/DoctorManagement/BreakManagement'))
);

const LeaveManagement = Loadable(
  lazy(() => import('pages/DoctorManagement/LeaveManagement'))
);

const DoctorFullSchedule = Loadable(
  lazy(() => import('pages/DoctorManagement/DoctorFullSchedule'))
);

const SlotPreview = Loadable(
  lazy(() => import('pages/DoctorManagement/SlotPreview'))
);

const AvailabilityCalendar = Loadable(
  lazy(() => import('pages/DoctorManagement/AvailabilityCalendar'))
);

// ==============================|| ENTERPRISE ERP (PHASE 1) ||============================== //

const ErpCompanies = Loadable(
  lazy(() => import('pages/EnterpriseERP/Companies'))
);

const ErpParties = Loadable(
  lazy(() => import('pages/EnterpriseERP/Parties'))
);

const ErpSettings = Loadable(
  lazy(() => import('pages/EnterpriseERP/Settings'))
);

// ==============================|| ENTERPRISE ERP (PHASE 2) ||============================== //

const ErpPurchaseOrders = Loadable(
  lazy(() => import('pages/EnterpriseERP/PurchaseOrders'))
);

const ErpGoodsReceiptNotes = Loadable(
  lazy(() => import('pages/EnterpriseERP/GoodsReceiptNotes'))
);

const ErpVendorBills = Loadable(
  lazy(() => import('pages/EnterpriseERP/VendorBills'))
);

// ==============================|| ROLE HELPERS ||============================== //

const getStoredRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || 'null');
    return getCurrentAccess(user || {}).role;

  } catch {
    return '';
  }
};

const ProtectedRoute = ({ permission, anyPermission, allPermissions, children }) => {
  return (
    <Authorized 
      permission={permission} 
      anyPermission={anyPermission} 
      allPermissions={allPermissions} 
      fallback={<Navigate to="/" replace />}
    >
      {children}
    </Authorized>
  );
};

// ==============================|| MAIN ROUTES ||============================== //

const MainRoutes = {

  path: '/',

  element: (
    <AuthGuard>
      <DashboardLayout />
    </AuthGuard>
  ),

  errorElement: <RouteErrorBoundary />,

  children: [

    {

      children: [

        // ================= DASHBOARD =================

        {
          index: true,

          element:
            getStoredRole().includes('doctor')
              ? <Navigate to="/dashboard/doctor" replace />
              : getStoredRole().includes('lab_assistant')
              ? <Navigate to="/dashboard/lab-assistant" replace />
              : getStoredRole().includes('pharmacist')
              ? <Navigate to="/dashboard/pharmacy" replace />
              : getStoredRole().includes('patient')
              ? <Navigate to="/dashboard/patient" replace />
              : <Navigate to="/dashboard/default" replace />
        },
        
        // ================= DASHBOARD REDIRECT =================
        {
          path: 'dashboard',
          element: <Navigate to="/" replace />
        },

        // ================= ADMIN DASHBOARD =================

        {
          path: 'dashboard/default',

          element:
            getStoredRole().includes('doctor')
              ? <Navigate to="/dashboard/doctor" replace />
              : getStoredRole().includes('lab_assistant')
              ? <Navigate to="/dashboard/lab-assistant" replace />
              : getStoredRole().includes('pharmacist')
              ? <Navigate to="/dashboard/pharmacy" replace />
              : getStoredRole().includes('patient')
              ? <Navigate to="/dashboard/patient" replace />
              : <DashboardDefault />
        },

        // ================= DOCTOR DASHBOARD =================

        {
          path: 'dashboard/doctor',
          element: <DoctorDashboard />
        },

        // ================= LAB ASSISTANT DASHBOARD =================

        {
          path: 'dashboard/lab-assistant',
          element: <LabAssistantDashboard />
        },

        // ================= PHARMACY DASHBOARD =================

        {
          path: 'dashboard/pharmacy',
          element: <PharmacyDashboard />
        },

        // ================= PATIENT DASHBOARD =================

        {
          path: 'dashboard/patient',
          element: <DashboardDefault />
        },

        // ================= PROFILE =================

        {
          path: 'profile',
          element: <ViewProfile />
        },

        {
          path: 'profile/edit',
          element: <EditProfile />
        },

        {
          path: 'change-password',
          element: <ChangePassword />
        },

        {
          path: 'clinic/setup',
          element: <ClinicSetupWizard />
        },

        {
          path: 'settings',
          element: <SettingsPage />
        },

        {
          path: 'clinic/profile',
          element: (
            <ProtectedRoute permission="clinics:read_own">
              <ClinicProfile />
            </ProtectedRoute>
          )
        },

        // ================= APPOINTMENTS =================

        {
          path: 'appointments',

          children: [

            {
              path: 'book',
              element: <CreateAppointment />
            },

            {
              path: 'my',
              element: <MyAppointments />
            },

            {
              path: 'review',

              element: (
                <ProtectedRoute anyPermission={['appointments:read_assigned', 'appointments:update_assigned', 'appointments:manage_own_clinic', 'appointments:read_own_clinic']}>
                  <AppointmentReview />
                </ProtectedRoute>
              )
            },

            {
              path: ':id', // Keep for detail views
              element: <AppointmentDetails />
            },
            
            {
              path: 'calendar',
              element: <AppointmentCalendar />
            },

            {
              path: 'queue',
              element: <AppointmentQueue />
            }

          ]
        },

        // ================= CLINICAL MANAGEMENT =================

        {
          path: 'clinical',

          children: [

            {
              path: 'medical-records',
              element: <ProtectedRoute permission="records:read_own_clinic"><MedicalRecords /></ProtectedRoute>
            },

            {
              path: 'prescriptions',
              element: <ProtectedRoute permission="prescriptions:read_own_clinic"><Prescriptions /></ProtectedRoute>
            },

            {
              path: 'lab-requests',
              element: (
                <SubscriptionModuleGuard module="lab_requests">
                  <ProtectedRoute 
                    anyPermission={[
                      'lab_requests:manage_own_clinic', 
                      'lab_requests:read_own_clinic', 
                      'lab_requests:read_assigned', 
                      'lab_requests:read_self'
                    ]}
                  >
                    <LabRequest />
                  </ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            },
            
            {
              path: 'lab-tests',
              element: (
                <SubscriptionModuleGuard module="lab_requests">
                  <ProtectedRoute permission="lab_tests:manage_own_clinic"><LabTests /></ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            },

            {
              path: 'medical-reports',
              element: (
                <ProtectedRoute permission="treatment_plans:read">
                  <TreatmentManagement />
                </ProtectedRoute>
              )
            },

            {
              path: 'workflow',
              element: <ClinicalWorkflow />
            },

            {
              path: 'external-lab-work',
              element: (
                <SubscriptionModuleGuard module="lab_requests">
                  <ExternalLabWork />
                </SubscriptionModuleGuard>
              )
            }

          ]
        },
        
        // ================= PATIENTS =================
        {
          path: 'patients',
          children: [
            {
              path: 'list',
              element: <PatientManagement />
            },
            {
              path: 'history',
              element: <PatientHistory />
            },
            {
              path: 'treatment-plans',
              element: <TreatmentManagement />
            }
          ]
        },

        // ================= PHARMACY =================
        {
          path: 'pharmacy',
          children: [
            {
              path: 'inventory',
              element: (
                <SubscriptionModuleGuard module="pharmacy">
                  <ProtectedRoute permission="inventory:read"><MedicineInventory /></ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            },
            {
              path: 'queue',
              element: (
                <SubscriptionModuleGuard module="pharmacy">
                  <ProtectedRoute permission="prescriptions:fulfill"><PrescriptionFulfillment /></ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            },
            {
              path: 'billing',
              element: (
                <SubscriptionModuleGuard module="pharmacy">
                  <ProtectedRoute permission="billing:manage"><PharmacyBilling /></ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            },
            {
              path: 'reports',
              element: (
                <SubscriptionModuleGuard module="pharmacy">
                  <ProtectedRoute permission="reports:read"><PharmacyReports /></ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            }
          ]
        },

        // ================= INVENTORY =================
        {
          path: 'inventory',
          children: [
            {
              path: 'tracking',
              element: <InventoryDashboard />
            },
            {
              path: 'stock',
              element: <InventoryDashboard />
            }
          ]
        },

        // ================= DOCTOR MANAGEMENT =================

        {
          path: 'doctors',

          children: [

            {
              path: 'list',
              element: <DoctorsList />
            },

            {
              path: 'availability',
              element: <AvailabilityManagement />
            },

            {
              path: 'breaks',
              element: <BreakManagement />
            },

            {
              path: 'leaves',
              element: <LeaveManagement />
            },

            {
              path: 'schedule',
              element: <DoctorFullSchedule />
            },

            {
              path: 'slots',
              element: <SlotPreview />
            }

          ]
        },

        // ================= ACCESS MANAGEMENT =================

        {
          path: 'access-management',
          children: [
            {
              path: 'modules',
              element: <ProtectedRoute permission="*"><ModulesUI /></ProtectedRoute>
            },
            {
              path: 'permissions',
              element: <ProtectedRoute permission="*"><PermissionsUI /></ProtectedRoute>
            },
            {
              path: 'roles',
              element: <ProtectedRoute permission="roles:assign_clinic"><RolePermissionsUI /></ProtectedRoute>
            },
            {
              path: 'clinic-type-permissions',
              element: <ProtectedRoute permission="*"><ClinicTypePermissionsUI /></ProtectedRoute>
            },
            {
              path: 'user-overrides',
              element: <ProtectedRoute permission="*"><UserPermissionOverridesUI /></ProtectedRoute>
            },
            {
              path: 'users',
              element: <ProtectedRoute permission="users:read"><UserManagement /></ProtectedRoute>
            },
            {
              path: 'role-permissions',
              element: <ProtectedRoute permission="roles:assign_clinic"><RolePermissionsUI /></ProtectedRoute>
            },
            {
              path: 'menus',
              element: <ProtectedRoute permission="roles:assign_clinic"><MenusUI /></ProtectedRoute>
            },
            {
              path: 'role-menus',
              element: <ProtectedRoute permission="roles:assign_clinic"><RoleMenusUI /></ProtectedRoute>
            }
          ]
        },

        // ================= ADMIN =================

        {
          path: 'admin',
          children: [
            {
              path: 'users',
              element: <ProtectedRoute permission="users:read"><UserManagement /></ProtectedRoute>
            },
            {
              path: 'doctors',
              element: <ProtectedRoute permission="doctors:manage_own_clinic"><DoctorManagement /></ProtectedRoute>
            },
            {
              path: 'patients',
              element: <ProtectedRoute permission="patients:read_own_clinic"><PatientManagement /></ProtectedRoute>
            },
            {
              path: 'staff',
              element: <ProtectedRoute permission="users:read"><StaffManagement /></ProtectedRoute>
            },
            {
              path: 'clinics',
              element: <ProtectedRoute permission="clinics:read_own"><ClinicManagement /></ProtectedRoute>
            },
            {
              path: 'clinic-types',
              element: <ProtectedRoute permission="clinics:read_own"><ClinicTypes /></ProtectedRoute>
            },
            {
              path: 'lab-tests',
              element: <ProtectedRoute permission="lab_tests:manage_own_clinic"><LabTests /></ProtectedRoute>
            }
          ]
        },

        {
          path: 'clinic-management',
          children: [
            {
              path: 'clinics',
              element: <ProtectedRoute permission="clinics:read"><ClinicManagement /></ProtectedRoute>
            }
          ]
        },
        
        // ================= OPERATIONS & HR =================
        {
          path: 'operations',
          children: [
            {
              path: 'staff',
              element: <ProtectedRoute permission="users:read"><StaffManagement /></ProtectedRoute>
            }
          ]
        },
        
        {
          path: 'hr',
          children: [
            {
              path: 'staff',
              element: <ProtectedRoute permission="users:read"><StaffManagement /></ProtectedRoute>
            },
            {
              path: 'attendance',
              element: <ProtectedRoute permission="users:read"><Typography variant="h3" sx={{p:3}}>Attendance Module (WIP)</Typography></ProtectedRoute>
            },
            {
              path: 'payroll',
              element: <ProtectedRoute permission="users:read"><Typography variant="h3" sx={{p:3}}>Payroll Module (WIP)</Typography></ProtectedRoute>
            }
          ]
        },
        
        // ================= INVENTORY =================
        {
          path: 'inventory',
          children: [
            {
              path: 'stock',
              element: <ProtectedRoute permission="inventory:read"><Typography variant="h3" sx={{p:3}}>Stock Module (WIP)</Typography></ProtectedRoute>
            },
            {
              path: 'suppliers',
              element: <ProtectedRoute permission="inventory:read"><Typography variant="h3" sx={{p:3}}>Suppliers Module (WIP)</Typography></ProtectedRoute>
            },
            {
              path: 'purchases',
              element: <ProtectedRoute permission="inventory:read"><Typography variant="h3" sx={{p:3}}>Purchases Module (WIP)</Typography></ProtectedRoute>
            }
          ]
        },
        
        // ================= BILLING =================
        {
          path: 'billing',
          children: [
            {
              path: 'quick-bill',
              element: (
                <SubscriptionModuleGuard module="billing">
                  <ProtectedRoute permission="billing:manage"><QuickBill /></ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            },
            {
              path: 'invoices',
              element: (
                <SubscriptionModuleGuard module="billing">
                  <ProtectedRoute permission="billing:manage"><InvoicesList /></ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            },
            {
              path: 'transactions',
              element: (
                <SubscriptionModuleGuard module="billing">
                  <Transactions />
                </SubscriptionModuleGuard>
              )
            },
            {
              path: 'payments',
              element: (
                <SubscriptionModuleGuard module="billing">
                  <Transactions />
                </SubscriptionModuleGuard>
              )
            },
            {
              path: 'expenses',
              element: (
                <SubscriptionModuleGuard module="billing">
                  <ProtectedRoute permission="billing:manage"><Expenses /></ProtectedRoute>
                </SubscriptionModuleGuard>
              )
            },
            {
              path: 'subscription',
              element: <Subscription />
            }
          ]
        },

        // ================= SUBSCRIPTION =================
        {
          path: 'subscription',
          element: <Subscription />
        },
        
        // ================= COMMUNICATION =================
        {
          path: 'communication',
          children: [
            {
              path: 'sms',
              element: <ProtectedRoute permission="*"><Typography variant="h3" sx={{p:3}}>SMS Module (WIP)</Typography></ProtectedRoute>
            },
            {
              path: 'whatsapp',
              element: <ProtectedRoute permission="*"><Typography variant="h3" sx={{p:3}}>WhatsApp Module (WIP)</Typography></ProtectedRoute>
            },
            {
              path: 'notifications',
              element: <ProtectedRoute permission="*"><Typography variant="h3" sx={{p:3}}>Notifications Module (WIP)</Typography></ProtectedRoute>
            }
          ]
        },
        
        // ================= REPORTS =================
        {
          path: 'reports',
          children: [
            {
              index: true,
              element: <ProtectedRoute anyPermission={['reports:read', 'revenue:read']}><ReportsDashboard /></ProtectedRoute>
            },
            {
              path: 'analytics',
              element: <ProtectedRoute anyPermission={['reports:read', 'revenue:read']}><ReportsDashboard /></ProtectedRoute>
            },
            {
              path: 'revenue',
              element: <ProtectedRoute anyPermission={['reports:read', 'revenue:read']}><ReportsDashboard /></ProtectedRoute>
            },
            {
              path: 'staff',
              element: <ProtectedRoute anyPermission={['reports:read', 'staff_reports:read']}><ReportsDashboard /></ProtectedRoute>
            },
            {
              path: 'pharmacy',
              element: <ProtectedRoute permission="reports:read"><PharmacyReports /></ProtectedRoute>
            }
          ]
        },

        // ================= ENTERPRISE ERP (PHASE 1) =================
        {
          path: 'enterprise-erp',
          children: [
            {
              index: true,
              element: <Navigate to="/enterprise-erp/companies" replace />
            },
            {
              path: 'companies',
              element: <ErpCompanies />
            },
            {
              path: 'parties',
              element: <ErpParties />
            },
            {
              path: 'settings',
              element: <ErpSettings />
            },
            {
              path: 'purchase-orders',
              element: <ErpPurchaseOrders />
            },
            {
              path: 'grn',
              element: <ErpGoodsReceiptNotes />
            },
            {
              path: 'vendor-bills',
              element: <ErpVendorBills />
            }
          ]
        },
        {
          path: 'enterprise_erp',
          element: <Navigate to="/enterprise-erp/companies" replace />
        },

        // ================= SAMPLE PAGE =================

        {
          path: 'sample-page',
          element: <SamplePage />
        },

        // ================= COMPONENTS =================

        {
          path: 'color',
          element: <Color />
        },

        {
          path: 'typography',
          element: <Typography />
        },

        {
          path: 'shadow',
          element: <Shadow />
        }

      ]
    }

  ]
};

export default MainRoutes;
